import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff, KeyRound, RefreshCw } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';
import { generateRandomPassword } from 'utils/passwordUtils';
import { controlId, errorId, validationMessage, type ValidationCode } from './validation';

interface PasswordFieldProps {
  value: string;
  onChange: (password: string) => void;
  /** Creation: always open and required. Edit: collapsed behind « Changer le mot de passe ». */
  creating: boolean;
  error?: ValidationCode;
}

/**
 * The password, typed or generated, the way the user dialogs do it. Every button is
 * `type="button"`: inside the record's <form>, only Save may submit.
 */
const PasswordField = ({ value, onChange, creating, error }: PasswordFieldProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(creating);
  const [shown, setShown] = useState(false);

  if (!open) {
    return (
      <div className="flex items-end sm:col-span-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          <KeyRound aria-hidden="true" />
          {t('Change the password')}
        </Button>
      </div>
    );
  }

  const generate = () => {
    onChange(generateRandomPassword());
    setShown(true);
  };

  // A refused copy needs no message: the value is on screen to copy by hand.
  const copy = () => navigator.clipboard?.writeText(value).catch(() => undefined);

  return (
    <div className="space-y-1.5 sm:col-span-2">
      <Label htmlFor={controlId('password')}>
        {creating ? t('Password') : t('New password')}
        {creating && <span className="text-rose-500"> *</span>}
      </Label>
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Input
            id={controlId('password')}
            type={shown ? 'text' : 'password'}
            value={value}
            required={creating}
            autoComplete="new-password"
            placeholder={creating ? undefined : t('Leave empty to keep unchanged')}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId('password') : undefined}
            className={cn('pr-10 font-mono', error && 'border-rose-400 focus-visible:ring-rose-400 dark:border-rose-500')}
            onChange={(event) => onChange(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setShown(!shown)}
            aria-label={shown ? t('Hide the password') : t('Show the password')}
            aria-pressed={shown}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm text-zinc-500 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            {shown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
        <Button type="button" variant="outline" onClick={generate} title={t('Generate a password')}>
          <RefreshCw aria-hidden="true" />
          {t('Generate')}
        </Button>
        {value && (
          <Button type="button" variant="outline" onClick={copy} title={t('Copy the password')}>
            <Copy aria-hidden="true" />
            {t('Copy')}
          </Button>
        )}
      </div>
      {error && (
        <p id={errorId('password')} className="text-xs text-rose-600 dark:text-rose-400">
          {validationMessage(t, error)}
        </p>
      )}
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        {value
          ? t('Pass it on to the technician: it will not be shown again.')
          : creating
            ? t('Use the button to generate a secure password')
            : t('Leave empty to keep unchanged, or generate a new secure password')}
      </p>
    </div>
  );
};

export default PasswordField;
